import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getRosterStudentRecords } from '../../../api/classRosterApi';
import { findPriorSemesterTexts, priorSemesterTarget } from '../../../utils/priorSemester';
import { trackSaenggibuUxEvent } from '../../../utils/eventTracker';
import './PriorSemesterOption.css';

/**
 * 2학기 명단을 골랐고 1년에 한 칸인 영역(행동특성)의 1학기 글이 있을 때만 보이는 체크 하나.
 * 1학기 글 자체는 AI에 보내지 않는다. 운영 1학기 글로 비교했을 때 넣어 쓰면 오히려 같은 사례를 되풀이하는 쪽으로 판정됐다.
 * value: { enabled, targetBytes } — 부모가 분량만 바꾼다.
 */
const PriorSemesterOption = ({ rosterId, students = [], category, schoolLevel, wordCount, onChange }) => {
  const [records, setRecords] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const shownRef = useRef('');

  useEffect(() => {
    setRecords(null);
    if (!rosterId) return undefined;
    let alive = true;
    getRosterStudentRecords(rosterId)
      .then((data) => { if (alive) setRecords(data); })
      .catch(() => { if (alive) setRecords(null); });
    return () => { alive = false; };
  }, [rosterId]);

  const roster = records?.roster;
  const bySid = useMemo(() => {
    if (!roster || Number(roster.semester) !== 2) return {};
    const all = findPriorSemesterTexts(records.students, { category, schoolYear: roster.school_year });
    const inBatch = new Set(students.map((student) => student.roster_sid).filter(Boolean));
    return Object.fromEntries(Object.entries(all).filter(([sid]) => inBatch.has(sid)));
  }, [category, records, roster, students]);
  const found = Object.keys(bySid).length;
  const target = useMemo(
    () => priorSemesterTarget(Object.values(bySid), { category, schoolLevel, wordCount }),
    [bySid, category, schoolLevel, wordCount],
  );

  useEffect(() => {
    onChange?.(target ? { enabled, targetBytes: enabled ? target.targetBytes : null } : null);
  }, [enabled, onChange, target]);

  useEffect(() => {
    if (!target) return;
    const key = `${rosterId}:${category}:${found}`;
    if (shownRef.current === key) return;
    shownRef.current = key;
    trackSaenggibuUxEvent('saenggibu_prior_semester', {
      saenggibu_category: category,
      is_batch: true,
      stage: 'shown',
      student_count: students.length,
      found_count: found,
    });
  }, [category, found, rosterId, students.length, target]);

  if (!target) return null;

  const toggle = () => {
    trackSaenggibuUxEvent('saenggibu_prior_semester', {
      saenggibu_category: category,
      is_batch: true,
      stage: enabled ? 'off' : 'on',
      found_count: found,
    });
    setEnabled((value) => !value);
  };

  return (
    <label className="prior-semester-option" data-testid="prior-semester-option">
      <input type="checkbox" checked={enabled} onChange={toggle} />
      <span>
        <strong>1학기 글 분량만큼 줄여 쓰기 ({found}명 1학기 글 있음)</strong>
        <span>
          {enabled
            ? `행동특성은 1년에 한 칸(${target.limit}바이트)이에요. 1학기 글이 ${target.priorBytes}바이트쯤이라, 합쳐도 넘치지 않게 ${target.targetBytes}바이트로 2학기를 써요.`
            : `체크하면 1학기 글과 합쳐 ${target.limit}바이트를 넘지 않게 남은 분량으로 써요.`}
        </span>
      </span>
    </label>
  );
};

export default PriorSemesterOption;
